import React, { useEffect, useState } from "react";

// ──────────────────────────────────────────────
// InstallAppPage.jsx — PWA 설치 안내 (?page=install)
// 사용자에게 이 링크만 보내면 플랫폼별로 설치를 안내:
//   · 안드로이드 Chrome → beforeinstallprompt 로 원탭 설치 버튼
//   · iOS Safari → 공유 → "홈 화면에 추가" 단계 안내 (프로그래매틱 불가)
//   · 카카오톡 등 인앱 브라우저 → 설치 불가 → 기본 브라우저로 열기 유도
// ──────────────────────────────────────────────

const INK = "#16151a";
const DARK = "#1b1a20";
const MUTED = "#6b7280";
const BODY = "#4b5563";
const LINE = "#ecedf2";

function detectEnv() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (ua.includes("mac") && navigator.maxTouchPoints > 1); // iPadOS 데스크톱 UA
  const isAndroid = /android/.test(ua);
  const inApp =
    /kakaotalk|naver\(inapp|instagram|fbav|fban|line\//.test(ua) ||
    (isAndroid && /; wv\)/.test(ua)); // Android WebView
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  return {
    isIOS,
    isAndroid,
    inApp,
    isStandalone,
    isKakao: /kakaotalk/.test(ua),
  };
}

const isLoginHSHost = /(^|\.)loginhs\./i.test(window.location.hostname);
const BRAND = isLoginHSHost ? "LoginHS" : "DocuAI2";
const BRAND_DESC = isLoginHSHost
  ? "HS 품목분류 · 세번 조회"
  : "범용 문서인식 시스템";

const InstallAppPage = () => {
  const [env] = useState(detectEnv);
  const [canPrompt, setCanPrompt] = useState(!!window.__pwaInstallPrompt);
  const [installed, setInstalled] = useState(
    !!window.__pwaInstalled || env.isStandalone,
  );

  useEffect(() => {
    document.title = `${BRAND} 앱 설치`;
    const onReady = () => setCanPrompt(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("pwa-install-ready", onReady);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-install-ready", onReady);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const p = window.__pwaInstallPrompt;
    if (!p) return;
    p.prompt();
    try {
      const { outcome } = await p.userChoice;
      if (outcome === "accepted") setInstalled(true);
    } catch (_e) {
      /* 사용자가 닫음 */
    }
    window.__pwaInstallPrompt = null;
    setCanPrompt(false);
  };

  // 카카오톡 인앱 → 기본 브라우저로 강제 오픈
  const openExternal = () => {
    const url = window.location.href;
    if (env.isKakao) {
      window.location.href =
        "kakaotalk://web/openExternal?url=" + encodeURIComponent(url);
      return;
    }
    if (env.isAndroid) {
      window.location.href =
        "intent://" +
        window.location.host +
        "/?page=install#Intent;scheme=https;package=com.android.chrome;end";
    }
  };

  const stepStyle = {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: "14px 0",
    borderBottom: `1px solid ${LINE}`,
  };
  const numStyle = {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: DARK,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        color: INK,
        fontFamily:
          "'Pretendard', -apple-system, 'Segoe UI', 'Malgun Gothic', sans-serif",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ maxWidth: 440, width: "100%", padding: "48px 24px" }}>
        {/* 브랜드 */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={`/pwa/icon-${isLoginHSHost ? "loginhs" : "docuai"}-192.png`}
            alt=""
            width={56}
            height={56}
            style={{ borderRadius: 14 }}
          />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }}>
              {BRAND}
            </div>
            <div style={{ fontSize: 13, color: MUTED }}>{BRAND_DESC}</div>
          </div>
        </div>

        <h1 style={{ fontSize: 24, margin: "28px 0 8px", letterSpacing: -0.5 }}>
          홈 화면에 앱으로 설치
        </h1>
        <p style={{ fontSize: 14, color: BODY, margin: 0, lineHeight: 1.6 }}>
          앱스토어 없이 설치됩니다. 홈 화면 아이콘으로 바로 실행되고, 항상 최신
          버전이 유지됩니다.
        </p>

        <div style={{ marginTop: 28 }}>
          {installed ? (
            <div
              style={{
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                padding: "18px 20px",
                fontSize: 14.5,
                lineHeight: 1.6,
              }}
            >
              <b>설치되어 있습니다.</b>
              <div style={{ color: BODY, marginTop: 4 }}>
                홈 화면에서 {BRAND} 아이콘을 눌러 실행하세요.
              </div>
            </div>
          ) : env.inApp ? (
            /* ── 인앱 브라우저 (카카오톡 등): 설치 불가 → 외부 브라우저 유도 ── */
            <div>
              <div
                style={{
                  border: `1px solid ${LINE}`,
                  background: "#fafbfd",
                  borderRadius: 12,
                  padding: "16px 18px",
                  fontSize: 13.5,
                  color: BODY,
                  lineHeight: 1.6,
                }}
              >
                지금은 <b>앱 내 브라우저</b>(카카오톡 등)로 열려 있어 설치할 수
                없습니다. 기본 브라우저로 열면 설치할 수 있습니다.
              </div>
              <button
                onClick={openExternal}
                style={{
                  marginTop: 16,
                  width: "100%",
                  padding: "15px 0",
                  borderRadius: 12,
                  border: "none",
                  background: DARK,
                  color: "#fff",
                  fontSize: 15.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {env.isIOS ? "Safari로 열기" : "Chrome으로 열기"}
              </button>
              <p style={{ fontSize: 12.5, color: MUTED, marginTop: 10 }}>
                버튼이 동작하지 않으면 우측 상단 메뉴에서 “다른 브라우저로
                열기”를 선택하세요.
              </p>
            </div>
          ) : env.isIOS ? (
            /* ── iOS: 수동 단계 안내 (Safari) ── */
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>
                iPhone · iPad — Safari에서
              </div>
              <div style={stepStyle}>
                <span style={numStyle}>1</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.6 }}>
                  하단(또는 상단)의 <b>공유 버튼</b>{" "}
                  <span
                    style={{
                      display: "inline-block",
                      border: `1.5px solid ${INK}`,
                      borderRadius: 4,
                      width: 18,
                      height: 18,
                      textAlign: "center",
                      lineHeight: "15px",
                      fontSize: 12,
                    }}
                  >
                    ↑
                  </span>
                  을 누르세요
                </span>
              </div>
              <div style={stepStyle}>
                <span style={numStyle}>2</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.6 }}>
                  아래로 내려 <b>“홈 화면에 추가”</b>를 누르세요
                </span>
              </div>
              <div style={{ ...stepStyle, borderBottom: "none" }}>
                <span style={numStyle}>3</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.6 }}>
                  오른쪽 위 <b>“추가”</b>를 누르면 끝 — 홈 화면에서 {BRAND}를
                  실행하세요
                </span>
              </div>
              <p style={{ fontSize: 12.5, color: MUTED, marginTop: 12 }}>
                Chrome 등 다른 브라우저를 쓰신다면 Safari로 열어주세요 — iOS에서
                홈 화면 추가는 Safari에서 가장 안정적입니다.
              </p>
            </div>
          ) : canPrompt ? (
            /* ── 안드로이드/데스크톱 Chrome: 원탭 설치 ── */
            <button
              onClick={handleInstall}
              style={{
                width: "100%",
                padding: "16px 0",
                borderRadius: 12,
                border: "none",
                background: DARK,
                color: "#fff",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              앱 설치하기
            </button>
          ) : (
            /* ── 프롬프트 미지원/미발화: 수동 안내 ── */
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>
                Android — Chrome에서
              </div>
              <div style={stepStyle}>
                <span style={numStyle}>1</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.6 }}>
                  오른쪽 위 <b>메뉴(⋮)</b>를 누르세요
                </span>
              </div>
              <div style={{ ...stepStyle, borderBottom: "none" }}>
                <span style={numStyle}>2</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.6 }}>
                  <b>“앱 설치”</b> 또는 <b>“홈 화면에 추가”</b>를 누르세요
                </span>
              </div>
              <p style={{ fontSize: 12.5, color: MUTED, marginTop: 12 }}>
                삼성 인터넷은 하단 메뉴() → “현재 페이지 추가” → “홈 화면”.
              </p>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 36,
            paddingTop: 16,
            borderTop: `1px solid ${LINE}`,
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12.5,
            color: MUTED,
          }}
        >
          <a href="/" style={{ color: MUTED, textDecoration: "none" }}>
            ← {BRAND} 바로 사용하기
          </a>
          <span>{window.location.hostname}</span>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPage;
